<template>
    <div class="main-container purger-legal-page">
        <section class="purger-legal-hero section-space--pt_90 section-space--pb_40">
            <div class="container">
                <div class="purger-legal-header">
                    <router-link class="purger-back" to="/apps/purger"><i class="fa fa-arrow-left"></i> Back</router-link>
                    <h1>Terms and Condition - Purger for Plex</h1>
                </div>
            </div>
        </section>

        <section class="section-space--pb_120">
            <div class="container">
                <div class="purger-legal-content">
                    <h1>Terms and Conditions</h1>
                    <p class="last-updated">Last updated: January 13, 2026</p>

                    <p>Welcome to Purger for Plex ("App", "we", "our", or "us"). These Terms and Conditions ("Terms") govern your use of our iOS application and services.</p>

                    <p><strong>By downloading, installing, or using Purger for Plex, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the App.</strong></p>

                    <h2>1. Description of Service</h2>
                    <p>Purger for Plex is an iOS application that allows users to connect to their Plex Media Server accounts, browse their media libraries through a swipe-based interface, and manage their Plex media collections.</p>

                    <h2>2. User Accounts and Authentication</h2>

                    <h3>2.1 Plex Account Required</h3>
                    <p>To use the App, you must have a valid Plex account and access to a Plex Media Server. You are responsible for maintaining the confidentiality of your Plex account credentials.</p>

                    <h3>2.2 Authentication</h3>
                    <p>The App uses Plex's OAuth2 authentication system to securely connect to your account. <strong>We do not store or keep your Plex authentication tokens on our servers</strong> - they are stored locally and securely only on your device. We use Firebase Anonymous Authentication to manage app functionality.</p>

                    <h3>2.3 Account Security</h3>
                    <p>You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use of your account.</p>

                    <h2>3. User Responsibilities</h2>

                    <h3>3.1 Media Deletion</h3>
                    <p>You acknowledge that:</p>
                    <ul>
                        <li>Deleting media through the App will permanently remove files from your Plex Media Server</li>
                        <li>You are solely responsible for backing up your media files</li>
                        <li>We are not liable for any data loss resulting from your use of the deletion feature</li>
                        <li>Deleted items may be recoverable through the History feature (Pro users only) with an undo function, but this is not guaranteed</li>
                    </ul>

                    <h3>3.2 Server Access</h3>
                    <p>You must have proper authorization to access and modify content on any Plex Media Server you connect to through the App. Unauthorized access or modification of server content is prohibited.</p>

                    <h3>3.3 Network Usage</h3>
                    <p>The App may access your Plex Media Server via local network or remote connections. You are responsible for any data charges incurred through your internet service provider.</p>

                    <h2>4. Privacy and Data Collection</h2>

                    <h3>4.1 Data We Collect</h3>
                    <p><strong>All analytics and crash reports are completely anonymous.</strong> We collect and process the following information:</p>
                    <ul>
                        <li>Plex authentication tokens (stored locally on your device only - we do NOT keep these)</li>
                        <li>Firebase Anonymous Authentication ID for app functionality</li>
                        <li>Anonymous usage analytics through Firebase Analytics</li>
                        <li>Anonymous crash reports and error logs through Sentry</li>
                        <li>Purchase history through RevenueCat (managed by Apple)</li>
                    </ul>

                    <h3>4.2 Third-Party Services</h3>
                    <p>The App uses the following third-party services:</p>
                    <ul>
                        <li><strong>Plex</strong> - Media server authentication and content access</li>
                        <li><strong>RevenueCat</strong> - Subscription and payment management</li>
                        <li><strong>Firebase</strong> - Analytics, authentication, and remote configuration</li>
                        <li><strong>Sentry</strong> - Error tracking and crash reporting</li>
                    </ul>
                    <p>Each service has its own privacy policy and terms of service.</p>

                    <h3>4.3 Data Storage</h3>
                    <p>Authentication tokens and user preferences are stored locally on your device using iOS UserDefaults. <strong>We do not store your Plex credentials, authentication tokens, or media content on our servers.</strong></p>

                    <h2>5. Intellectual Property</h2>

                    <h3>5.1 App Ownership</h3>
                    <p>The App and all related intellectual property rights are owned by us. You are granted a limited, non-exclusive, non-transferable license to use the App for personal, non-commercial purposes.</p>

                    <h3>5.2 Plex Trademark</h3>
                    <p>Purger for Plex is an independent application and is not affiliated with, endorsed by, or sponsored by Plex Inc. "Plex" is a registered trademark of Plex Inc.</p>

                    <h3>5.3 User Content</h3>
                    <p>You retain all rights to your media content. We do not claim any ownership rights to the media files stored on your Plex Media Server.</p>

                    <h2>6. Disclaimer of Warranties</h2>
                    <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
                    <ul>
                        <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                        <li>Any warranty that the App will be uninterrupted, secure, or error-free</li>
                        <li>Any warranty regarding the quality, accuracy, or reliability of the App</li>
                    </ul>

                    <h2>7. Limitation of Liability</h2>
                    <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                    <ul>
                        <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                        <li>WE SHALL NOT BE LIABLE FOR ANY LOSS OF DATA, MEDIA FILES, OR SERVER CONTENT</li>
                        <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE APP IN THE PAST 12 MONTHS</li>
                    </ul>

                    <h2>8. Prohibited Uses</h2>
                    <p>You agree not to:</p>
                    <ul>
                        <li>Use the App for any illegal purpose or in violation of any laws</li>
                        <li>Access Plex servers you do not own or have permission to access</li>
                        <li>Attempt to reverse engineer, decompile, or disassemble the App</li>
                        <li>Use the App to infringe on intellectual property rights</li>
                        <li>Circumvent any limitations or restrictions we impose on free users</li>
                        <li>Use automated systems to interact with the App</li>
                    </ul>

                    <h2>9. Governing Law</h2>
                    <p>These Terms shall be governed by and construed in accordance with the laws of Italy, without regard to its conflict of law provisions.</p>

                    <h2>10. Contact Information</h2>
                    <div class="contact">
                        <p><strong>Developer:</strong> DualBit</p>
                        <p><strong>Email:</strong> <a href="mailto:dualbit@dualb.it">dualbit@dualb.it</a></p>
                        <p><strong>Support:</strong> For questions or concerns about these Terms, please contact us at the email address above.</p>
                    </div>

                    <h2>11. Severability</h2>
                    <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>

                    <h2>12. Entire Agreement</h2>
                    <p>These Terms constitute the entire agreement between you and us regarding the use of the App and supersede all prior agreements and understandings.</p>

                    <hr class="purger-legal-divider">

                    <p class="purger-legal-footer">(c) 2026 DualBit. All rights reserved.<br>Purger for Plex is not affiliated with Plex Inc.</p>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
    export default {
        name: 'PurgerTerms',
        metaInfo: {
            title: 'Terms and Condition - Purger for Plex',
            htmlAttrs: {
                lang: 'en',
                amp: true
            }
        }
    }
</script>

<style lang="scss" scoped>
    .purger-legal-hero {
        padding-top: 160px;
    }

    .purger-legal-header {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .purger-back {
        color: #27397c;
        font-weight: 600;
    }

    .purger-legal-content {
        max-width: 900px;
    }

    .purger-legal-content h1 {
        font-size: 30px;
        margin-bottom: 10px;
    }

    .purger-legal-content h2 {
        font-size: 22px;
        margin-top: 28px;
    }

    .purger-legal-content h3 {
        font-size: 18px;
        margin-top: 22px;
    }

    .last-updated {
        color: #666;
    }

    .purger-legal-divider {
        margin: 40px 0;
        border: none;
        border-top: 1px solid #ddd;
    }

    .purger-legal-footer {
        text-align: center;
        color: #666;
        font-size: 14px;
    }

    @media (max-width: 767px) {
        .purger-legal-hero {
            padding-top: 90px;
        }
    }
</style>
